/* eslint jsx-a11y/anchor-is-valid: 0 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import DateTime from '../../../DateTime'
import { useApi } from '../../../../hooks'
import IncidentDetailsPanel from '../IncidentDetails'
import UserDetails from '../../../UserDetails'
import CreateIncidentAttachment from '../../../CreateIncidentAttachment'
import AddDispatchOrganization from '../../../AddDispatchOrganization'
import CreateIncidentMapMarker from '../CreateIncidentMapMarker'
import Button from '../../../Button'
import { db } from '../../../../firebase' // Adjust the import based on your project structure

import './styles.scss'
import { collection, doc, getFirestore, onSnapshot } from 'firebase/firestore'
import useGetUserData from '../../../../hooks/useGetUserData'
import { ApiContext } from '../../../../hooks/api'

function Incident({
  incident,
  selectIncident,
  isSelected,
  openDetails,
  openUserDetails,
  profile
}) {
  const {
    name,
    createdAt,
    createdBy,
    acknowledgements = [],
    description
  } = incident
  const { user, locading } = useGetUserData(createdBy)
  const acknowledgedBy = acknowledgements[0]?.user
  const currentDispatch = useMemo(() => {
    if (!incident.acceptedBy || !profile) return null

    // Find most recent acceptance for this user
    const userEntries = incident.acceptedBy
      .filter((entry) => entry.userId === profile.id)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

    return userEntries[0] || null
  }, [incident, profile])

  const statusText = useMemo(() => {
    if (incident.status === 'ended') {
      return { text: 'Ended', color: '#ef4444' }
    }
    const isUserResponding = currentDispatch !== null

    // Check if any users have accepted this incident
    const hasResponders = incident.acceptedBy?.length > 0

    if (isUserResponding) {
      return { text: 'Accepted', color: '#3FC083' }
    }

    if (hasResponders) {
      return { text: 'In-Progress', color: '#f59e0b' }
    }

    if (incident.status === 'inprogress') {
      return { text: 'In-Progress', color: '#f59e0b' }
    }
    return { text: 'Pending', color: '#3b82f6' }
  }, [currentDispatch, incident.status])

  return (
    <div
      className={`card incident-card ${isSelected && 'active'}`}
      onClick={() => selectIncident(incident)}>
      <div className='card-content block'>
        <div
          className='content w-[100%]'
          style={{ display: 'flex', flexDirection: 'row' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: '5px'
            }}>
            <span>{name || description || 'Untitle Incident'}</span>
            {user && (
              <>
                <span>
                  Created by&nbsp;
                  <strong>
                    <a onClick={() => openUserDetails(user)}>
                      {user?.displayName || `User #${user.id}`}
                    </a>
                  </strong>
                </span>
              </>
            )}
            {acknowledgedBy && (
              <>
                <span>
                  Acknowledged by{' '}
                  <strong>
                    <a onClick={() => openUserDetails(acknowledgedBy)}>
                      {acknowledgedBy.personalData?.displayName?.value ||
                        `User #${acknowledgedBy.id}`}
                    </a>
                  </strong>
                </span>
              </>
            )}
            <span>
              <DateTime value={createdAt} />
            </span>
            <span style={{ color: statusText.color }}>{statusText.text}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Button className='is-link' onClick={() => openDetails(incident)}>
              DETAILS
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ({
  incidents: initialIncidents,
  setActivePanel,
  openSecondaryPanel,
  setMapCenter,
  setMapZoom,
  mapSelection,
  dispatchBindings,
  profile,
  setCustomActiveChannel,
  selectedIncident,
  setSelectedIncident,
  setDirectionsDestination,
  userTypes
}) {
  const [client] = useApi()
  const [incidents, setIncidents] = useState(initialIncidents)
  const { socket } = useContext(ApiContext) // Add this line
  const [users, setUsers] = useState([])
  const myOrgId = profile?.organization?.id

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        const userList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        setUsers(userList)
      },
      (err) => console.error('Error fetching users:', err)
    )

    return () => unsubscribe()
  }, [])

  const orgUsers = useMemo(
    () => (myOrgId ? users.filter((u) => u.organization?.id === myOrgId) : []),
    [users, myOrgId]
  )

  useEffect(() => {
    const unsubscribeFunctions = []

    Object.keys(initialIncidents).forEach((incidentId) => {
      const incidentRef = doc(db, 'incidents', incidentId)
      const unsubscribe = onSnapshot(
        incidentRef,
        (doc) => {
          if (doc.exists()) {
            const data = doc.data()
            setIncidents((prevIncidents) => ({
              ...prevIncidents,
              [incidentId]: {
                ...data,
                id: doc.id,
                createdAt: data.createdAt
                  ? new Date(data.createdAt)
                  : new Date() // Ensure createdAt is a Date object
              }
            }))
          } else {
            console.warn(`Incident ${incidentId} does not exist`)
          }
        },
        (error) => {
          console.error(`Error listening to incident ${incidentId}:`, error)
        }
      )
      unsubscribeFunctions.push(unsubscribe)
    })

    return () => {
      unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
    }
  }, [initialIncidents])

  const emitToOrganizationUsers = useCallback(() => {
    if (!socket) return
    if (!orgUsers?.length) {
      return
    }

    orgUsers?.forEach((u) => {
      socket.emit('join_user_location_update', u.id)
    })
  }, [socket, orgUsers])

  const showAllOnMap = useCallback(() => {
    setSelectedIncident(null)
    setMapCenter(null)
    emitToOrganizationUsers()
  }, [setSelectedIncident, setMapCenter, emitToOrganizationUsers])

  useEffect(() => {
    if (selectedIncident) {
      const location = selectedIncident.locations.find(
        (loc) => loc.format === 'lat-long'
      )
      if (location) {
        setMapCenter({
          lng: location.longitude,
          lat: location.latitude
        })
      }
    }
  }, [selectedIncident, setMapCenter])

  const sorted = Object.values(incidents)
    .filter(
      (incident) =>
        incident.status !== 'ended' &&
        (incident?.createdBy === profile?.id ||
          (Array.isArray(incident.dispatchOrganizations) &&
            incident.dispatchOrganizations.some((org) => org.id == myOrgId)))
    )
    .map((incident) => ({
      ...incident,
      createdAt: new Date(incident.createdAt)
    }))
    .sort((a, b) => b.createdAt - a.createdAt)

  // An incident that ends -- ended by this user or by anyone else -- drops out
  // of the list above, but the map went on following the copy held in
  // selectedIncident: its responders, pins and tracks stayed on screen frozen
  // until something else was clicked. Fall back to showing everything, exactly
  // as the "Show all on map" button does. The mobile app's IncidentBloc falls
  // back the same way, for the same reason.
  const selectedIsOngoing = selectedIncident
    ? sorted.some((incident) => incident.id === selectedIncident.id)
    : true

  useEffect(() => {
    if (!selectedIsOngoing) showAllOnMap()
  }, [selectedIsOngoing, showAllOnMap])

  const openDetails = useCallback(
    (incident) =>
      openSecondaryPanel(
        <IncidentDetailsPanel
          incident={incident}
          openSecondaryPanel={openSecondaryPanel}
          mapSelection={mapSelection}
          dispatchBindings={dispatchBindings}
          profile={profile}
          setCustomActiveChannel={setCustomActiveChannel}
          setActivePanel={setActivePanel}
          setDirectionsDestination={setDirectionsDestination}
          userTypes={userTypes}
        />,
        // incident.name
      ),
    [
      openSecondaryPanel,
      mapSelection,
      dispatchBindings,
      profile,
      setCustomActiveChannel,
      setActivePanel,
      setDirectionsDestination,
      userTypes
    ]
  )

  const openUserDetails = useCallback(
    (user) =>
      openSecondaryPanel(
        <UserDetails id={user.id} />,
        user.personalData?.displayName?.value || `User #${user.id}`
      ),
    [openSecondaryPanel]
  )

  const addAttachment = useCallback(
    (incidentId) =>
      openSecondaryPanel(
        <CreateIncidentAttachment
          incidentId={incidentId}
          onAttachmentCreated={() =>
            openSecondaryPanel(
              <IncidentDetailsPanel
                incident={incidents[incidentId]}
                openSecondaryPanel={openSecondaryPanel}
                mapSelection={mapSelection}
                dispatchBindings={dispatchBindings}
              />
            )
          }
        />,
        'Create an attachment'
      ),
    [openSecondaryPanel, incidents, dispatchBindings, mapSelection]
  )

  const addMarker = useCallback(
    (incidentId) =>
      openSecondaryPanel(
        <CreateIncidentMapMarker
          incidentId={incidentId}
          mapSelection={mapSelection}
          onMarkerCreated={() => openSecondaryPanel(null)}
        />,
        'Create a map marker'
      ),
    [openSecondaryPanel, mapSelection]
  )

  const addDispatchOrganization = useCallback(
    (incidentId) =>
      openSecondaryPanel(
        <AddDispatchOrganization
          incidentId={incidentId}
          dispatchBindings={dispatchBindings}
          onDispatchOrganizationCreated={() => openSecondaryPanel(null)}
        />,
        'Add responders'
      ),
    [openSecondaryPanel, dispatchBindings]
  )

  const cancelIncident = useCallback(
    async (incidentId) => {
      try {
        if (window.confirm('Are you sure you wish to cancel this incident?')) {
          await client(`admin/incidents/${incidentId}/status`, {
            status: 'ended'
          })
        }
      } catch (err) {
        console.error(err)
      }
    },
    [client]
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        position: 'relative'
      }}>
      <div
        className={`card incident-card ${selectedIncident === null && 'active'
          }`}
        onClick={() => showAllOnMap()}>
        <div className='card-content w-full'>
          <div className='content w-full'>
            <Button
              className='is-link is-fullwidth'
              onClick={() => showAllOnMap()}>
              SHOW ALL ON MAP
            </Button>
          </div>
        </div>
      </div>

      {sorted.map((incident) => (
        <Incident
          key={incident.id}
          incident={incident}
          isSelected={selectedIncident?.id === incident.id}
          selectIncident={setSelectedIncident}
          openDetails={openDetails}
          openUserDetails={openUserDetails}
          addAttachment={addAttachment}
          addMarker={addMarker}
          addDispatchOrganization={addDispatchOrganization}
          cancelIncident={cancelIncident}
          setMapCenter={setMapCenter}
          mapSelection={mapSelection}
          profile={profile}
        />
      ))}
      {sorted.length !== 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            flexDirection: 'column',
            paddingBottom: '2rem'
          }}>
          <div style={{ fontStyle: 'italic', fontWeight: 600 }}>
            Click on an incident to select it.
          </div>
        </div>
      )}
      <div className='add-incident-btn'>
        <Button
          onClick={() => setActivePanel('new-incident')}
          icon='fa fa-plus'
          className='is-link'
          style={{
            width: '62px',
            height: '62px',
            fontSize: '2rem'
          }}></Button>
      </div>
    </div>
  )
}
